import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import { Navigation } from '../../components/Navigation';
import { Calendar, MapPin, DollarSign, CheckCircle, XCircle, Clock, FileText } from 'lucide-react';

interface ApplicationWithJob {
  id: string;
  status: string;
  created_at: string;
  job: {
    id: string;
    title: string;
    company: string;
    location: string;
    salary_min: number | null;
    salary_max: number | null;
  };
}

export function MyApplications() {
  const { profile } = useAuthStore();
  const [applications, setApplications] = useState<ApplicationWithJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  useEffect(() => {
    if (profile) {
      loadApplications();
    }
  }, [profile, filterStatus]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('applications')
        .select(`
          id,
          status,
          created_at,
          job:job_id (
            id,
            title,
            company,
            location,
            salary_min,
            salary_max
          )
        `)
        .eq('applicant_id', profile!.id)
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data, error } = await query;

      if (error) throw error;
      setApplications((data as unknown as ApplicationWithJob[]) || []);
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-orange-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-orange-100 text-orange-800 border-orange-300';
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'Congratulations! Your application has been accepted.';
      case 'rejected':
        return 'Unfortunately, your application was not selected this time.';
      default:
        return 'Your application is being reviewed by the employer.';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Applications</h1>
              <p className="text-gray-600 mt-1">Track the status of your job applications</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 mb-6 border-2 border-gray-200">
            <div className="flex items-center gap-3">
              <span className="font-semibold text-gray-700">Filter:</span>
              <div className="flex gap-2">
                {['all', 'pending', 'accepted', 'rejected'].map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    className={`px-4 py-2 rounded-lg font-medium transition ${
                      filterStatus === status
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {applications.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No applications yet</h2>
              <p className="text-gray-600 mb-6">Start applying to jobs to track your applications here</p>
              <a
                href="/swipe"
                className="inline-block px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition"
              >
                Discover Jobs
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((application) => (
                <div key={application.id} className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200 hover:border-orange-300 transition">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{application.job.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border-2 flex items-center gap-1 ${getStatusColor(application.status)}`}>
                          {getStatusIcon(application.status)}
                          <span>{application.status}</span>
                        </span>
                      </div>

                      <p className="text-lg text-gray-700 font-medium mb-3">{application.job.company}</p>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{application.job.location}</span>
                        </div>
                        {application.job.salary_min && application.job.salary_max && (
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            <span>${(application.job.salary_min / 1000).toFixed(0)}k - ${(application.job.salary_max / 1000).toFixed(0)}k</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>Applied {new Date(application.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>

                      <div className={`p-3 rounded-lg ${
                        application.status === 'accepted' ? 'bg-green-50' :
                        application.status === 'rejected' ? 'bg-red-50' : 'bg-orange-50'
                      }`}>
                        <p className="text-sm text-gray-700">{getStatusMessage(application.status)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition">
                      View Job Details
                    </button>
                    {application.status === 'accepted' && (
                      <button className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition">
                        Message Employer
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
